import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { NoMediaIcon } from './Icon';

type CroppableImageProps = {
  image: CroppableImage;
  container?: {
    width: number;
    height: number;
  };
  alt?: string;
  // Whether the whole image should be displayed when cropped into it's new container. If the cropped image
  // doesn't fit within the container, the cropped image will be centered in its new container with empty
  // bars on either the x or y axis. Defaults to false, which centers the smallest possible fit of the cropped
  // image over the container, filling it in its entirety.
  contain?: boolean;
};

/**
 * Crops an image based on a passed in specification. If no specification is passed, returns
 * the original image. The specification (spec) includes the x and y coordinates, which is the
 * starting point for the cropped image (offset of the original image), and the height and width
 * of the cropped image.
 *
 * Note: When not passing container dimensions, really consider whether this component should be
 * used at all.
 */
export const CroppableImage = ({ image, container, alt, contain = false }: CroppableImageProps) => {
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const [containerDimensions, setContainerDimensions] = useState(container);
  const [hasErrored, setHasErrored] = useState(false);

  const { src, spec } = image;
  const shouldCrop = Boolean(spec) && !(spec?.width === 0 || spec?.height === 0);

  useEffect(() => {
    const imageContainer = imageContainerRef?.current;
    const parentElement = imageContainer?.parentElement;
    if (!shouldCrop || !imageContainer || !parentElement || Boolean(container)) {
      return;
    }

    const handleResize = () => {
      const rect = parentElement.getBoundingClientRect();
      setContainerDimensions({ width: rect.width, height: rect.height });
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [container, shouldCrop]);

  const getDimensions = () => {
    if (!spec || !containerDimensions) {
      return {
        offset: { top: 0, left: 0 },
        scale: 1,
      };
    }

    const { height, width } = containerDimensions;

    // The ratio of the desired container against the specification container. This expresses
    // how much the spec's container should shrink (or grow) to accommodate our container's
    // height or width
    const ratio = {
      x: width / spec.width,
      y: height / spec.height,
    };

    // In order to fill all available space in the scaled container, determine whether
    // aspect ratio should be expressed with relation to height or width.
    const scale = contain ? Math.min(ratio.x, ratio.y) : Math.max(ratio.x, ratio.y);

    const scaledWidth = spec.width * scale;
    const scaledHeight = spec.height * scale;

    return {
      scaledWidth,
      scaledHeight,
      // Ensure that the scaled image is centered over or in its container
      offset: {
        left: (width - scaledWidth) / 2,
        top: (height - scaledHeight) / 2,
      },
      scale,
    };
  };

  const { scaledWidth, scaledHeight, offset, scale } = getDimensions();

  if (hasErrored) {
    return <NoMediaIcon />;
  }

  return (
    <ImageContainer $height={containerDimensions?.height} ref={imageContainerRef}>
      <ImageWrap $width={scaledWidth} $height={scaledHeight} $offset={offset}>
        <CroppedImage
          src={src}
          $x={spec?.x ? -1 * spec.x : 0}
          $y={spec?.y ? -1 * spec.y : 0}
          $scale={scale}
          shouldCrop={shouldCrop}
          alt={alt}
          onError={() => setHasErrored(true)}
        />
      </ImageWrap>
    </ImageContainer>
  );
};

const ImageContainer = styled.div<{ $height?: number }>`
  height: ${({ $height }) => ($height ? `${$height}px` : '100%')};
  overflow: hidden;
  position: relative;
  width: 100%;
`;

const ImageWrap = styled.div<{
  $height?: number;
  $width?: number;
  $offset: { left: number; top: number };
}>`
  height: ${({ $height }) => ($height ? `${$height}px` : '100%')};
  left: ${({ $offset }) => `${$offset.left}px`};
  overflow: hidden;
  position: absolute;
  top: ${({ $offset }) => `${$offset.top}px`};
  width: ${({ $width }) => ($width ? `${$width}px` : '100%')};
`;

const CroppedImage = styled.img<{ $x: number; $y: number; $scale: number; shouldCrop: boolean }>`
  object-fit: cover;
  object-position: ${({ $x, $y }) => `${$x}px ${$y}px`};
  transform-origin: top left;
  transform: ${({ $scale }) => `scale(${$scale})`};

  ${({ shouldCrop }) =>
    !shouldCrop &&
    css`
      height: 100%;
      width: 100%;
      object-position: 50% 50%;
    `}
`;
